/**
 * Types voor klant-documenttemplates (S6) — opdrachtovereenkomst /
 * opdrachtbevestiging. Gedeeld tussen API en backoffice.
 * Zie docs/uploads/document-templates-opdrachtovereenkomst.md.
 */
import type {
  PlaceholderConfig,
  DocumentTemplateCategory,
  DocumentTemplateScope,
  TemplateScanStatusDTO,
} from './document-templates'
import type { OnboardingDocumentType } from './onboarding-documenten'
import type { DocumentEntiteitType } from './documenten'
import type { BriefpapierKeuzeInvoer, BriefpapierStandaardDTO } from './briefpapier'

export type GeneratedDocStatus =
  | 'CONCEPT'
  | 'DEFINITIEF'
  | 'VERZONDEN'
  | 'ONDERTEKEND'
  | 'VERLOPEN'
  // batch-55 bruikleen-docs (FO-421) — vrijgegeven mét ondertekenplicht, nog
  // niet getekend (nooit stil definitief).
  | 'WACHT_OP_ONDERTEKENING'

export const GENERATED_DOC_STATUSES: readonly GeneratedDocStatus[] = [
  'CONCEPT',
  'DEFINITIEF',
  'VERZONDEN',
  'ONDERTEKEND',
  'VERLOPEN',
  'WACHT_OP_ONDERTEKENING',
] as const

export const GENERATED_DOC_STATUS_LABELS: Record<GeneratedDocStatus, string> = {
  CONCEPT: 'Concept',
  DEFINITIEF: 'Definitief',
  VERZONDEN: 'Verzonden',
  ONDERTEKEND: 'Ondertekend',
  VERLOPEN: 'Verlopen',
  WACHT_OP_ONDERTEKENING: 'Wacht op ondertekening',
}

/** Eén bijlage-referentie binnen het template (bv. de Algemene Voorwaarden-PDF). */
export interface TemplateBijlage {
  storageKey: string
  fileName: string
}

/**
 * FO-377 - welke briefpapier-bron de generatie voor dit sjabloon gebruikt:
 * SJABLOON = de DOCX draagt zelf briefpapier (wint altijd), ENTITEIT = het
 * standaard-huisstijl-briefpapier (BV of algemeen STREEP), GEEN = kale
 * generatie (zichtbare markering in het templatebeheer).
 */
export type TemplateHuisstijlBron = 'SJABLOON' | 'ENTITEIT' | 'GEEN'

export interface ClientDocumentTemplateDTO {
  id: string
  naam: string
  slug: string
  beschrijving: string | null
  /** Bestandsnaam van de geüploade DOCX; null tot een admin het bestand uploadt. */
  templateFileName: string | null
  /** Heeft het template al een geüpload DOCX-bestand? */
  heeftTemplateBestand: boolean
  /**
   * FO-377 - draagt de geüploade DOCX zelf briefpapier (header-/footer-
   * afbeeldingen)? Null = nog niet gescand of geen bestand.
   */
  heeftBriefpapier: boolean | null
  /**
   * FO-410 (batch-54) - de BEHEERDE vlag "dit sjabloon heeft eigen briefpapier".
   * Staat hij uit (default), dan rendert elke generatie op het STREEP-/entiteit-
   * briefpapier, ook als de DOCX toevallig een eigen header draagt. Verschilt
   * bewust van `heeftBriefpapier`: dat is de gescande waarneming, dit is de keuze.
   */
  eigenBriefpapier: boolean
  /**
   * batch-55 bruikleen-docs (FO-421) - onderteken-default per sjabloon. Aan
   * (default) = de bruikleen-wizard vinkt "laten ondertekenen" standaard aan;
   * de keuze bij het genereren is de override.
   */
  ondertekeningStandaard: boolean
  /** FO-377 - de briefpapier-bron die de generatie zal gebruiken; null = geen DOCX. */
  huisstijlBron: TemplateHuisstijlBron | null
  /** Rijke per-veld-configuratie (verving het oude string[]-`placeholders`). */
  placeholderConfig: PlaceholderConfig[]
  category: DocumentTemplateCategory
  /** Waar het template aangeboden wordt: relatie, medewerker of onboarding. */
  scope: DocumentTemplateScope
  /** Alleen bij scope=ONBOARDING: het gekoppelde onboarding-documenttype. */
  onboardingType: OnboardingDocumentType | null
  /** Variant-discriminator (bv. ContractType-waarde bij ARBEIDSOVEREENKOMST); null = generiek. */
  variant: string | null
  sortOrder: number
  bijlagen: TemplateBijlage[]
  actief: boolean
  /**
   * D.7: een auto-geconverteerd template is een concept en wordt door de
   * onboarding-generator overgeslagen tot een admin het na Word-review live zet.
   */
  isConcept: boolean
  entityId: string | null
  entityNaam: string | null
  /**
   * FO-453 (batch-61) - de laatste scan-synchronisatie van de DOCX (tagfouten,
   * nieuwe/verdwenen keys). Null = nog nooit gescand sinds de sync bestaat.
   */
  scan: TemplateScanStatusDTO | null
  /**
   * FO-459 (batch-62) - de briefpapier-standaard van dit template (variant +
   * vaste BV of "volg de context"); null = volgt de standaard van de documentsoort.
   */
  briefpapierStandaard: BriefpapierStandaardDTO | null
  createdAt: string
  updatedAt: string
}

export interface ClientGeneratedDocumentDTO {
  id: string
  templateId: string
  templateNaam: string
  templateCategory: DocumentTemplateCategory
  /** Null bij de klant-loze generate-flow (geen gekoppelde relatie). */
  clientId: string | null
  clientNaam: string | null
  werkbonId: string | null
  /** FO-139 - gekoppelde medewerker (medewerker-overeenkomsten); null bij klantdocumenten. */
  employeeId: string | null
  employeeNaam: string | null
  /**
   * batch-55 bruikleen-docs (FO-421) - herkomst: voor welk gebruiksitem dit
   * document is gegenereerd (VOERTUIG of VOORRAAD_UNIEK_ITEM); null = geen item.
   */
  itemType: DocumentEntiteitType | null
  itemId: string | null
  documentId: string
  /** Bestandsnaam van de gegenereerde PDF (voor de download-knop). */
  fileName: string
  status: GeneratedDocStatus
  placeholderValues: Record<string, string>
  ondertekendDoor: string | null
  ondertekendOp: string | null
  createdBy: string
  createdAt: string
}

/** Body voor POST /clients/:clientId/documents/generate. */
export interface GenerateClientDocumentBody {
  templateId: string
  /** Geselecteerde STREEP-BV (vult de STREEP_*-placeholders). */
  entityId: string
  /** Optioneel: koppel aan een werkbon (opdrachtbevestiging). */
  werkbonId?: string | null
  /** Handmatige waarden/overrides — worden over de auto-fill gelegd. */
  placeholderValues?: Record<string, string>
  /** FO-459 (batch-62) - briefpapierkeuze uit de kiezer; null/leeg = de cascade kiest. */
  briefpapier?: BriefpapierKeuzeInvoer | null
}

/** Body voor PATCH van de status of ondertekening van een gegenereerd document. */
export interface UpdateGeneratedDocumentBody {
  status?: GeneratedDocStatus
  ondertekendDoor?: string | null
  ondertekendOp?: string | null
}

/**
 * Metadata-update voor een template (DOCX-bestand gaat via aparte upload-route,
 * placeholderConfig via PUT …/placeholder-config).
 */
export interface UpdateClientDocumentTemplateBody {
  naam?: string
  beschrijving?: string | null
  category?: DocumentTemplateCategory
  scope?: DocumentTemplateScope
  sortOrder?: number
  actief?: boolean
  /** D.7: publiceer een concept-template (admin zet 'm na Word-review live). */
  isConcept?: boolean
  /**
   * FO-410 (batch-54): markeer dit sjabloon als "heeft eigen briefpapier". Uit
   * (default) = de generatie zet het op het STREEP-/BV-briefpapier.
   */
  eigenBriefpapier?: boolean
  /** batch-55 (FO-421): onderteken-default van het sjabloon (bruikleen-wizard). */
  ondertekeningStandaard?: boolean
  entityId?: string | null
  /**
   * batch-67 hr-brieven (FO-506): welke brief binnen de categorie
   * non-actiefstelling. `null` = de brief van de non-actiefstelling zelf,
   * 'OPHEFFING' = de brief van de opheffing. Alleen bij die categorie.
   */
  variant?: string | null
}

/**
 * Menselijke labels voor de bekende placeholders (spec stap 2). De modal toont
 * deze als veldlabel; onbekende placeholders vallen terug op de ruwe sleutel.
 */
export const PLACEHOLDER_LABELS: Record<string, string> = {
  OPDRACHTGEVER_NAAM: 'Opdrachtgever - naam',
  OPDRACHTGEVER_KVK: 'Opdrachtgever - KvK-nummer',
  OPDRACHTGEVER_POSTCODE: 'Opdrachtgever - postcode',
  OPDRACHTGEVER_PLAATS: 'Opdrachtgever - plaats',
  // FO-410 (batch-54): `..._STRAAT` draagt uitsluitend de straatnaam; het
  // huisnummer staat los en de samenstelling in `..._ADRES`.
  OPDRACHTGEVER_STRAAT: 'Opdrachtgever - straat',
  OPDRACHTGEVER_HUISNUMMER: 'Opdrachtgever - huisnummer',
  OPDRACHTGEVER_ADRES: 'Opdrachtgever - straat + huisnummer',
  OPDRACHTGEVER_POSTCODE_PLAATS: 'Opdrachtgever - postcode + plaats',
  OPDRACHTGEVER_VERTEGENWOORDIGER: 'Opdrachtgever - vertegenwoordiger',
  OPDRACHTGEVER_VERTEGENWOORDIGER_FUNCTIE: 'Opdrachtgever - functie vertegenwoordiger',
  OPDRACHTGEVER_CONTACTPERSOON: 'Opdrachtgever - contactpersoon',
  STREEP_ENTITEIT: 'STREEP - entiteit',
  STREEP_KVK: 'STREEP - KvK-nummer',
  STREEP_ADRES: 'STREEP - adres',
  STREEP_POSTCODE_PLAATS: 'STREEP - postcode + plaats',
  DATUM: 'Datum',
  DATUM_ONDERTEKENING: 'Datum ondertekening',
  PLAATS_ONDERTEKENING: 'Plaats ondertekening',
  REFERENTIENUMMER: 'Referentienummer',
  LOCATIE_ADRES: 'Locatie / adres',
  FUNCTIE: 'Functie',
  STARTDATUM: 'Startdatum',
  DUUR: 'Duur',
  WERKTIJDEN: 'Werktijden / ploegendienst',
  CONTACTPERSOON_OPDRACHTGEVER: 'Contactpersoon opdrachtgever',
  BASISTARIEF: 'Basistarief (€/uur)',
  REISUREN_TARIEF: 'Reisurentarief (€/uur)',
  KILOMETERVERGOEDING: 'Kilometervergoeding (€/km)',
  HOOFDOVEREENKOMST_REF: 'Referentie hoofdovereenkomst',
}
